/**
 * FirstTimeTooltip Component
 *
 * Displays a floating tooltip hint for first-time users.
 * Used to guide users on how to interact with expansion icons.
 * Uses a portal to render at document body level to avoid overflow clipping.
 */

import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './FirstTimeTooltip.css';

interface FirstTimeTooltipProps {
  /** Whether the tooltip should be visible */
  show: boolean;
  /** The tooltip message to display */
  message: string;
  /** Position relative to the target element */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional className for additional styling */
  className?: string;
}

export const FirstTimeTooltip: React.FC<FirstTimeTooltipProps> = ({
  show,
  message,
  position = 'top',
  className = '',
}) => {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!show || !markerRef.current) return;

    const updatePosition = () => {
      const marker = markerRef.current;
      if (!marker) return;

      const parent = marker.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipWidth = tooltipEl?.offsetWidth || 300;
      const tooltipHeight = tooltipEl?.offsetHeight || 40;

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - tooltipHeight - 8 + window.scrollY;
          left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
          break;
        case 'bottom':
          top = rect.bottom + 8 + window.scrollY;
          left = rect.left + window.scrollX;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
          left = rect.left - tooltipWidth - 8 + window.scrollX;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
          left = rect.right + 8 + window.scrollX;
          break;
      }

      // Ensure tooltip doesn't go off screen
      const padding = 10;
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }

      setCoords({ top, left });
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered before measuring
    const frameId = requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show, position]);

  if (!show) {
    return null;
  }

  // Choose the appropriate pointing finger based on tooltip position
  const getIcon = () => {
    switch (position) {
      case 'top':
        return '👇'; // Tooltip above, point down
      case 'bottom':
        return '👆'; // Tooltip below, point up
      case 'left':
        return '👉'; // Tooltip on left, point right
      case 'right':
        return '👈'; // Tooltip on right, point left
      default:
        return '👆';
    }
  };

  const tooltip = (
    <div
      ref={tooltipRef}
      className={`first-time-tooltip first-time-tooltip-portal first-time-tooltip-${position} ${className}`}
      style={coords ? { top: coords.top, left: coords.left } : { top: -9999, left: -9999 }}
    >
      <span className="first-time-tooltip-icon">{getIcon()}</span>
      <span className="first-time-tooltip-message">{message}</span>
    </div>
  );

  return (
    <>
      {/* Invisible marker to get position from */}
      <span ref={markerRef} style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0 }} />
      {ReactDOM.createPortal(tooltip, document.body)}
    </>
  );
};

export default FirstTimeTooltip;
